import {
  useState,
  PropsWithChildren,
  useMemo,
  useEffect,
  useCallback,
} from "react";

import {
  Button,
  Checkbox,
  CircularProgress,
  Grid,
  TextField,
  Typography,
} from "@material-ui/core";

import { MUTATIONS } from "../../../../graphql";
import {
  BucketAttachment,
  Communication,
  CommunicationCommunicationGroup,
  CommunicationData,
  CommunicationTarget,
} from "../../../../graphql/models";

import useCommunicationFormStyle from "./communications_form.style";

import {
  AttachmentsPicker,
  CommunicationTargetsPicker,
  ConfirmButtonWithDialog,
  I18NMarkdownEditor,
  I18NTextField,
} from "../../../components";
import { useSnackbarMutation } from "../../../../hooks";
import { i18nForFields } from "../../../../i18n";

export interface CommunicationEditFormProps extends PropsWithChildren<any> {
  value: Communication | CommunicationData | undefined;
  onValueUpdatedSuccessfully: (v: Communication) => void;
}

export function CommunicationForm(props: CommunicationEditFormProps) {
  const { value, onValueUpdatedSuccessfully } = props;
  const classes = useCommunicationFormStyle();

  let mutation = MUTATIONS.CREATE_COMMUNICATION;
  if (value && (value as Communication).id) {
    mutation = MUTATIONS.UPDATE_COMMUNICATION;
  }

  const [
    invokeCreateOrUpdateCommunication,
    { loading, data: createOrUpdateCommunicationData },
  ] = useSnackbarMutation(mutation);
  const [updated, setUpdated] = useState(false);

  const [name, setName] = useState<string>(value?.name ?? "");
  // const [subject, setSubject] = useState<string>(
  //   value?.subject ?? ''
  // );
  // const [message, setMessage] = useState<string>(
  //   value?.message ?? ''
  // );
  const [i18nFields, setI18nFields] = useState<
    Partial<i18nForFields<"subject" | "message">>
  >(value?.i18n ?? {});
  const [draft, setDraft] = useState<boolean>(value?.draft ?? true);
  const [targets, setCommunicationTargets] = useState<CommunicationTarget[]>(
    value?.targets ?? []
  );
  const [communicationGroups, setCommunicationGroups] = useState<
    CommunicationCommunicationGroup[]
  >(value?.groups ?? []);
  const [attachments, setAttachments] = useState<BucketAttachment[]>(
    value?.attachments ?? []
  );
  const [isCommercial, setIsCommercial] = useState<boolean>(
    value?.isCommercial ?? false
  );

  const [invokeSubmit, setInvokeSubmit] = useState(false);

  const onSaveAndSendConfirm = () => {
    setDraft(false);
    setInvokeSubmit(true);
  };

  const setGroupsAndTargets = (
    groups: CommunicationCommunicationGroup[],
    targets: CommunicationTarget[]
  ) => {
    setCommunicationGroups(groups);
    setCommunicationTargets(targets);
  };

  const onFormSubmit = useCallback(
    (evt?) => {
      evt?.preventDefault();
      setInvokeSubmit(false);
      invokeCreateOrUpdateCommunication({
        variables: {
          id: (value as Communication).id,
          communicationData: {
            name,
            draft,
            targets,
            attachments,
            groups: communicationGroups.map(
              (v) =>
                ({
                  id: v.id,
                  color: v.color,
                  name: v.name,
                } as CommunicationCommunicationGroup)
            ),
            i18n: i18nFields,
            isCommercial,
          },
        },
      });
    },
    [
      attachments,
      communicationGroups,
      draft,
      i18nFields,
      invokeCreateOrUpdateCommunication,
      name,
      targets,
      value,
      isCommercial,
    ]
  );

  if (createOrUpdateCommunicationData && !updated) {
    onValueUpdatedSuccessfully(
      createOrUpdateCommunicationData.createSystemCommunication
    );
    setUpdated(true);
  }

  useEffect(() => {
    if (invokeSubmit) {
      onFormSubmit();
    }
  }, [invokeSubmit, onFormSubmit]);

  const isDisabled = useMemo(() => {
    return value?.draft === false;
  }, [value]);

  return (
    <>
      {loading && `Loading...`}
      {!createOrUpdateCommunicationData && (
        <form
          className={classes.root}
          onSubmit={onFormSubmit}
          noValidate
          autoComplete="off"
        >
          <Grid container spacing={3}>
            <Grid item sm={12} xs={12}>
              <TextField
                required
                disabled={isDisabled}
                className={classes.textField}
                label="Nome"
                variant="outlined"
                value={name}
                onChange={(e) => setName(e.target.value)}
              />
            </Grid>
            <Grid item sm={12} xs={12}>
              <I18NTextField
                required
                i18nFieldName="subject"
                disabled={isDisabled}
                className={classes.textField}
                label="Oggetto"
                variant="outlined"
                value={i18nFields}
                onChange={(e) => setI18nFields(e)}
              />
            </Grid>
            <Grid item sm={12} xs={12}>
              <I18NMarkdownEditor
                disabled={isDisabled}
                i18nFieldName="message"
                value={i18nFields}
                onChange={(e) => setI18nFields(e)}
              />
              <br />
              <Checkbox
                edge="start"
                checked={isCommercial}
                onChange={(e, checked) => setIsCommercial(checked)}
                tabIndex={-1}
                disableRipple
              />{" "}
              Comunicazione commerciale
              {isCommercial && (
                <Typography variant="caption">
                  <br />
                  (Questa comunicazione verrà inviata solo agli utenti che al
                  momento dell'invio avranno accettato la sezione 3 della
                  privacy policy relativa alla ricezione di comunicazioni
                  commerciali.)
                </Typography>
              )}
            </Grid>
            {/* {(targets.length > 0) && (
              <Grid item sm={12} xs={12}>
                <List 
                  subheader={<ListSubheader>Destinatari</ListSubheader>} 
                  className={classes.list}
                >
                  {targets.map(target => (
                    <ListItem>
                      <ListItemText 
                        primary={target.name} 
                      />
                    </ListItem>
                  ))}
                </List>
              </Grid>
            )} */}
            {/* {(targets.length === 0) && (
              <Grid item sm={12} xs={12}>
                <CommunicationTargetsPicker
                  groups={communicationGroups}
                  groupsChange={(groups, targets) => setGroupsAndTargets(
                    groups, targets
                  )}
                  targets={targets}
                  targetsChange={setCommunicationTargets}
                />
              </Grid>
            )} */}
            <Grid item sm={12} xs={12}>
              <CommunicationTargetsPicker
                disabled={isDisabled}
                groups={communicationGroups}
                groupsChange={(groups, targets) =>
                  setGroupsAndTargets(groups, targets)
                }
                targets={targets}
                targetsChange={setCommunicationTargets}
              />
            </Grid>
            <Grid item sm={12} xs={12}>
              <AttachmentsPicker
                defaultValue={attachments}
                onUpdated={(v) => setAttachments(v)}
                disabled={isDisabled}
              />
            </Grid>
            <Grid item sm={12} xs={12}>
              <ConfirmButtonWithDialog
                className={classes.textField}
                disabled={isDisabled}
                type="button"
                variant="contained"
                color="secondary"
                onUndo={() => void 0}
                onConfirm={onSaveAndSendConfirm}
                dialogProps={{
                  fullScreen: false,
                  title: "Conferma invio comunicazione",
                }}
                dialogContent={
                  <Typography variant="body2">
                    Confermi di voler salvare ed inviare la comunicazione
                    immediatamente?
                  </Typography>
                }
              >
                {!loading ? "Salva e invia" : <CircularProgress />}
              </ConfirmButtonWithDialog>
              <br /> <br />
              <Button
                className={classes.textField}
                disabled={isDisabled}
                type="submit"
                variant="contained"
                color="primary"
              >
                {!loading ? "Salva senza inviare" : <CircularProgress />}
              </Button>
            </Grid>
          </Grid>
        </form>
      )}
    </>
  );
}
